import React, { useState, useEffect } from 'react';
import { 
  Edit, 
  History, 
  FileText, 
  Calendar, 
  User, 
  Tag, 
  CheckCircle, 
  Clock,
  ArrowLeft,
  GitCompare,
  Trash2
} from 'lucide-react';
import { Button } from '../ui/button';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/authStore';
import { useMultipleCodelists } from '../../hooks/useCodelist';

interface Document {
  id: number;
  title: string;
  description?: string;
  category?: string;
  nis2_chapter?: string; // Keep for backward compatibility
  nis2_article?: string;
  nis2_letter?: string;
  status: 'draft' | 'review' | 'approved' | 'archived';
  version: number;
  created_by: number;
  creator_first_name: string;
  creator_last_name: string;
  updated_by?: number;
  updater_first_name?: string;
  updater_last_name?: string;
  approved_by?: number;
  approver_first_name?: string;
  approver_last_name?: string;
  approved_at?: string;
  created_at: string;
  updated_at: string;
  tags?: string[];
  review_date?: string;
  content?: string;
}

interface DocumentVersion {
  id: number;
  document_id: number;
  version: number;
  file_path: string;
  change_notes?: string;
  created_by: number;
  first_name: string;
  last_name: string;
  created_at: string;
}

interface DocumentViewerProps {
  documentId: number;
  onEdit?: () => void;
  onBack?: () => void;
  canEdit?: boolean;
  canApprove?: boolean;
}

export function DocumentViewer({ 
  documentId, 
  onEdit, 
  onBack, 
  canEdit = false,
  canApprove = false 
}: DocumentViewerProps) {
  const [document, setDocument] = useState<Document | null>(null);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [compareVersion, setCompareVersion] = useState<number | null>(null);
  const [versionContent, setVersionContent] = useState<string>('');
  const [diffData, setDiffData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showVersions, setShowVersions] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const { toast } = useToast();
  const { user } = useAuthStore();
  const { getItemName } = useMultipleCodelists(['nis2_articles', 'nis2_letters']);

  useEffect(() => {
    fetchDocument();
    fetchVersions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/documents/${documentId}?includeContent=true`) as any;
      setDocument(response);
    } catch (error) {
      console.error('Failed to fetch document:', error);
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se načíst dokument',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    try {
      const response = await api.get(`/documents/${documentId}/versions`) as any;
      setVersions(response.versions);
    } catch (error) {
      console.error('Failed to fetch versions:', error);
    }
  };

  const fetchVersionContent = async (version: number) => {
    try {
      const response = await api.get(`/documents/${documentId}/versions/${version}`) as any;
      setVersionContent(response.content);
      setSelectedVersion(version);
    } catch (error) {
      console.error('Failed to fetch version content:', error);
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se načíst obsah verze',
        variant: 'destructive'
      });
    }
  };

  const compareVersions = async (fromVersion: number, toVersion: number) => {
    try {
      const response = await api.get(`/documents/${documentId}/compare?from=${fromVersion}&to=${toVersion}&format=structured`) as any;
      setDiffData(response);
      setShowDiff(true);
    } catch (error) {
      console.error('Failed to compare versions:', error);
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se porovnat verze',
        variant: 'destructive'
      });
    }
  };

  const approveDocument = async () => {
    try {
      await api.post(`/documents/${documentId}/approve`);
      toast({
        title: 'Úspěch',
        description: 'Dokument byl schválen'
      });
      fetchDocument(); // Refresh document data
    } catch (error) {
      console.error('Failed to approve document:', error);
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se schválit dokument',
        variant: 'destructive'
      });
    }
  };

  const cleanupVersions = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Opravdu chcete smazat všechny verze kromě aktuální? Tato akce je nevratná.')) {
      return;
    }

    try {
      const response = await api.post(`/documents/${documentId}/cleanup-versions`) as any;
      toast({
        title: 'Verze vyčištěny',
        description: `Úspěšně smazáno ${response.deleted_versions} verzí`,
      });
      
      // Refresh versions and document
      await fetchVersions();
      await fetchDocument();
      
      // Clear selected version if it was deleted
      setSelectedVersion(null);
      setVersionContent('');
    } catch (error) {
      console.error('Failed to cleanup versions:', error);
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se vyčistit verze dokumentu',
        variant: 'destructive'
      });
    }
  };

  // No longer needed: ReactMarkdown handles proper rendering

  const renderDiff = (diff: any) => {
    if (!diff || !diff.hunks) return null;

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-green-600">+{diff.stats?.additions || 0} přidáno</span>
          <span className="text-red-600">-{diff.stats?.deletions || 0} odebráno</span>
          <span className="text-blue-600">{diff.stats?.changes || 0} změn celkem</span>
        </div>
        
        {diff.hunks.map((hunk: any, hunkIndex: number) => (
          <div key={hunkIndex} className="border border-gray-200 rounded">
            <div className="bg-gray-50 px-4 py-2 text-sm font-mono text-gray-600">
              @@ Lines {hunk.oldStart + 1}-{hunk.oldStart + hunk.lines.length} @@
            </div>
            <div className="font-mono text-sm">
              {hunk.lines.map((line: any, lineIndex: number) => {
                let className = 'px-4 py-1 ';
                let prefix = ' ';
                
                if (line.type === 'added') {
                  className += 'bg-green-50 text-green-800 border-l-4 border-green-400';
                  prefix = '+';
                } else if (line.type === 'removed') {
                  className += 'bg-red-50 text-red-800 border-l-4 border-red-400';
                  prefix = '-';
                } else {
                  className += 'bg-white';
                }
                
                return (
                  <div key={lineIndex} className={className}>
                    <span className="text-gray-400 mr-2">{prefix}</span>
                    {line.content}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'review': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'archived': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Návrh';
      case 'review': return 'Na schválení';
      case 'approved': return 'Schváleno';
      case 'archived': return 'Archivováno';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('cs-CZ');
  };

  // Helper function to remove YAML frontmatter from content
  const removeMetadata = (content: string) => {
    if (!content) return '';
    const lines = content.split('\n');
    
    // Check if content starts with YAML frontmatter
    if (lines[0] !== '---') {
      return content;
    }
    
    // Find end of YAML frontmatter
    let endIndex = -1;
    for (let i = 1; i < lines.length; i++) {
      if (lines[i] === '---') {
        endIndex = i;
        break;
      }
    }
    
    if (endIndex === -1) {
      return content;
    }
    
    // Return content after frontmatter, removing any leading empty lines
    const contentLines = lines.slice(endIndex + 1);
    while (contentLines.length > 0 && contentLines[0].trim() === '') {
      contentLines.shift();
    }
    
    return contentLines.join('\n');
  };

  // Wrap ASCII tables (bounded by dashed lines) into fenced code blocks for readability
  function formatAsciiTablesAsPre(markdown: string): string {
    const lines = markdown.split('\n');
    const result: string[] = [];
    let i = 0;
    while (i < lines.length) {
      if (/^\s*-{3,}\s*$/.test(lines[i])) {
        const startIndex = i;
        i++;
        while (i < lines.length && !/^\s*-{3,}\s*$/.test(lines[i])) {
          i++;
        }
        if (i < lines.length && /^\s*-{3,}\s*$/.test(lines[i])) {
          // include closing line
          const block = lines.slice(startIndex, i + 1).join('\n');
          result.push('```text');
          result.push(block);
          result.push('```');
          i++;
          continue;
        }
        // no closing line found, push the start line and continue
        result.push(lines[startIndex]);
      } else {
        result.push(lines[i]);
        i++;
      }
    }
    return result.join('\n');
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-96"></div>
          <div className="h-4 bg-gray-200 rounded w-64"></div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Dokument nenalezen</h2>
        <p className="text-gray-600">Požadovaný dokument neexistuje nebo nemáte oprávnění k jeho zobrazení.</p>
      </div>
    );
  }

  if (showDiff && diffData) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => setShowDiff(false)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zpět na dokument
            </Button>
            <h1 className="text-2xl font-bold">
              Porovnání verzí {diffData.from_version} a {diffData.to_version}
            </h1>
          </div>
          <div className="text-sm text-gray-600">
            Podobnost: {diffData.similarity}%
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{document.title}</CardTitle>
          </CardHeader>
          <CardContent>
            {renderDiff(diffData.diff)}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zpět
            </Button>
          )}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{document.title}</h1>
            {document.description && (
              <p className="text-gray-600 mt-1">{document.description}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge className={getStatusColor(document.status)}>
            {getStatusLabel(document.status)}
          </Badge>
          <span className="text-sm text-gray-500">Verze {document.version}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        {canEdit && onEdit && (
          <Button onClick={onEdit} className="flex items-center gap-2">
            <Edit className="h-4 w-4" />
            Upravit
          </Button>
        )}
        
        {canApprove && document.status === 'review' && (
          <Button onClick={approveDocument} variant="outline" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Schválit
          </Button>
        )}
        
        <Button 
          variant="outline" 
          onClick={() => setShowVersions(!showVersions)}
          className="flex items-center gap-2"
        >
          <History className="h-4 w-4" />
          Historie verzí ({versions.length})
        </Button>

        {user?.role === 'admin' && versions.length > 1 && (
          <Button 
            variant="outline" 
            onClick={cleanupVersions}
            className="flex items-center gap-2 text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
          >
            <Trash2 className="h-4 w-4" />
            Smazat staré verze
          </Button>
        )}
      </div>

      {/* Document Metadata */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <User className="h-4 w-4" />
                <span>Vytvořil: {document.creator_first_name} {document.creator_last_name}</span>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4" />
                <span>Vytvořeno: {formatDate(document.created_at)}</span>
              </div>
              
              {document.updated_by && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>
                    Upravil: {document.updater_first_name} {document.updater_last_name} 
                    ({formatDate(document.updated_at)})
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {document.category && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Tag className="h-4 w-4" />
                  <span>Kategorie: {document.category}</span>
                </div>
              )}
              
              {document.nis2_article && document.nis2_letter && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FileText className="h-4 w-4" />
                  <span>NIS2: {getItemName('nis2_articles', document.nis2_article)} - {getItemName('nis2_letters', document.nis2_letter)}</span>
                </div>
              )}
              
              {document.review_date && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span>Kontrola: {formatDate(document.review_date)}</span>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {document.approved_by && (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span>
                    Schválil: {document.approver_first_name} {document.approver_last_name}
                    ({formatDate(document.approved_at!)})
                  </span>
                </div>
              )}
              
              {document.tags && document.tags.length > 0 && (
                <div>
                  <div className="text-sm text-gray-600 mb-2">Štítky:</div>
                  <div className="flex flex-wrap gap-1">
                    {document.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Version History */}
      {showVersions && (
        <Card>
          <CardHeader>
            <CardTitle>Historie verzí</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {versions.map(version => (
                <div key={version.id} className="flex items-center justify-between p-4 border border-gray-200 rounded">
                  <div>
                    <div className="font-medium">Verze {version.version}</div>
                    <div className="text-sm text-gray-600">
                      {version.first_name} {version.last_name} • {formatDate(version.created_at)}
                    </div>
                    {version.change_notes && (
                      <div className="text-sm text-gray-500 mt-1">{version.change_notes}</div>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchVersionContent(version.version)}
                    >
                      Zobrazit
                    </Button>
                    
                    {compareVersion && compareVersion !== version.version && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => compareVersions(Math.min(compareVersion, version.version), Math.max(compareVersion, version.version))}
                      >
                        <GitCompare className="h-4 w-4 mr-1" />
                        Porovnat
                      </Button>
                    )}
                    
                    <Button
                      variant={compareVersion === version.version ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCompareVersion(compareVersion === version.version ? null : version.version)}
                    >
                      {compareVersion === version.version ? 'Zrušit porovnání' : 'Označit pro porovnání'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document Content */}
      <Card>
        <CardHeader>
          <CardTitle>
            Obsah dokumentu
            {selectedVersion && ` (Verze ${selectedVersion})`}
          </CardTitle>
          {selectedVersion && (
            <Button variant="outline" size="sm" onClick={() => {
              setSelectedVersion(null);
              setVersionContent('');
            }}>
              Zobrazit aktuální verzi
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, rehypeSanitize]}
            >
              {formatAsciiTablesAsPre(removeMetadata(selectedVersion ? versionContent : (document.content || '')))}
            </ReactMarkdown>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
